import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/icons';
import { AsyncPanel } from '@/components/platform/AsyncPanel';
import { Pagination, SortableHeader } from '@/components/platform/Pagination';
import { StateBadge } from '@/components/platform/StateBadge';
import { useEnumLabel, useInstitutionTypeLabel } from '@/components/platform/useLabel';
import { Modal } from '@/components/ui/Modal';
import { Tooltip } from '@/components/ui/Tooltip';
import { Select } from '@/components/ui/Select';
import { platformApi } from '@/api/platform';
import { useAsyncData } from '@/hooks';
import {
  DEFAULT_PAGE_SIZE,
  INSTITUTION_TYPES,
  ONBOARDING_STATES,
  TERMINAL_ONBOARDING_STATES,
  type CreateOnboardingCaseRequest,
  type InstitutionType,
  type OnboardingCaseSummary,
  type OnboardingState,
  type SortDirection,
  caseDisplayName,
} from '@/types/platform';
import { formatDate, formatRelative } from '@/utils/format';
import styles from './platform-pages.module.css';

/**
 * PA-02, the list half: find the case, then open it.
 *
 * WHERE THE WORK HAPPENS, AND WHY IT IS SAID OUT LOUD.
 * State filtering is a server request: OnboardingCaseController accepts `state`
 * and answers with every matching case. It does NOT accept a free-text query or
 * paging parameters — it validates with `forbidNonWhitelisted`, so the `q=` this
 * screen used to send produced a 400, and every search read as an outage.
 *
 * Search, sorting and paging are therefore applied here, over the COMPLETE result
 * set rather than over one page of it. That distinction is the whole point: the
 * previous "open cases" fold ran over whatever happened to have been returned and
 * reported a count that was right only while the list was small. Folding a
 * complete list is arithmetic; folding a page is a guess dressed up as a number.
 *
 * If the endpoint later grows paging, all three must move to the server on the
 * same day — a client-side sort over page one of nine is a wrong answer, not a
 * weaker feature.
 *
 * The default filter is OPEN, not ALL: a platform administrator opening this
 * screen is looking for work, and ACTIVE, REJECTED and CLOSED cases are history.
 */

type StateFilter = OnboardingState | 'ALL' | 'OPEN';

const OPEN_STATES = ONBOARDING_STATES.filter((state) => !TERMINAL_ONBOARDING_STATES.includes(state));

/**
 * A case whose target activation date has passed and which is not live yet.
 *
 * Terminal cases are excluded on purpose: a case rejected in March did not "miss"
 * its April target, and flagging it would fill the column with rows nobody can act
 * on.
 */
function isOverdue(entry: OnboardingCaseSummary): boolean {
  if (!entry.targetActivationDate) return false;
  if (TERMINAL_ONBOARDING_STATES.includes(entry.state)) return false;
  return new Date(entry.targetActivationDate).getTime() < Date.now();
}

/**
 * Sortable columns, limited to fields the LIST endpoint actually returns.
 *
 * `outstandingActionCount` used to be one of them and core has never sent it —
 * the case list is read without relations, so the column was permanently a dash
 * and sorting by it did nothing at all. `targetActivationDate` replaces it: it is
 * on every case, and a case past its target date is the one worth chasing.
 */
type CaseSortField = 'legalName' | 'state' | 'updatedAt' | 'targetActivationDate';

export function OnboardingCasesPage() {
  const { t, i18n } = useTranslation();
  const label = useEnumLabel();
  const typeLabel = useInstitutionTypeLabel();
  const navigate = useNavigate();
  const locale = i18n.language;

  const [stateFilter, setStateFilter] = useState<StateFilter>('OPEN');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [sort, setSort] = useState<{ field: CaseSortField; order: SortDirection }>({
    field: 'updatedAt',
    order: 'DESC',
  });

  const requestState = stateFilter === 'OPEN' ? 'ALL' : stateFilter;
  const key = `cases:${stateFilter}`;
  const fetcher = useCallback(() => platformApi.onboarding.listCases({ state: requestState }), [requestState]);
  const { state, reload } = useAsyncData<OnboardingCaseSummary[]>(key, fetcher);

  const matching = useMemo(() => {
    if (state.status !== 'ready') return [];
    const needle = search.trim().toLowerCase();
    const rows =
      stateFilter === 'OPEN'
        ? state.data.filter((entry) => !TERMINAL_ONBOARDING_STATES.includes(entry.state))
        : state.data;
    if (needle.length === 0) return rows;
    return rows.filter((entry) =>
      [entry.legalName, entry.displayName, entry.institutionCode, entry.caseReference, entry.contactEmail]
        .filter((value): value is string => typeof value === 'string')
        .some((value) => value.toLowerCase().includes(needle)),
    );
  }, [state, stateFilter, search]);

  const sorted = useMemo(() => {
    const direction = sort.order === 'ASC' ? 1 : -1;
    return [...matching].sort((a, b) => compareCases(a, b, sort.field) * direction);
  }, [matching, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / DEFAULT_PAGE_SIZE));
  // A filter change can leave the reader on a page that no longer exists. Snapping
  // to the last page is less disorienting than an empty table, which reads as
  // "nothing matched".
  const currentPage = Math.min(page, totalPages);
  const visible = sorted.slice((currentPage - 1) * DEFAULT_PAGE_SIZE, currentPage * DEFAULT_PAGE_SIZE);

  const changeSort = (field: string, order: SortDirection) => {
    setSort({ field: field as CaseSortField, order });
    setPage(1);
  };

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>{t('platform.cases.title')}</h1>
          <p className={styles.pageLead}>{t('platform.cases.lead')}</p>
        </div>
        <button type="button" className={styles.primaryBtn} onClick={() => setCreateOpen(true)}>
          <Icon name="plus" size={16} />
          {t('platform.cases.create')}
        </button>
      </header>

      <form
        className={styles.filterBar}
        onSubmit={(event) => {
          event.preventDefault();
          setSearch(searchInput.trim());
          setPage(1);
        }}
        role="search"
      >
        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="case-state">
            {t('platform.cases.filterState')}
          </label>
          <Select
            id="case-state"
            value={stateFilter}
            onChange={(value) => {
              setStateFilter(value as StateFilter);
              setPage(1);
            }}
            options={[
              { value: 'OPEN', label: t('platform.cases.stateOpen') },
              { value: 'ALL', label: t('platform.cases.stateAll') },
              ...ONBOARDING_STATES.map((value) => ({ value, label: label('state.onboarding', value) })),
            ]}
          />
        </div>

        <div className={styles.filterField}>
          <label className={styles.filterLabel} htmlFor="case-search">
            {t('platform.cases.searchLabel')}
          </label>
          <input
            id="case-search"
            className={styles.input}
            type="search"
            value={searchInput}
            placeholder={t('platform.cases.searchPlaceholder')}
            aria-describedby="case-search-hint"
            onChange={(event) => setSearchInput(event.target.value)}
          />
          <span className={styles.fieldHint} id="case-search-hint">
            {t('platform.cases.searchHint')}
          </span>
        </div>

        <button type="submit" className={styles.secondaryBtn}>
          <Icon name="filter" size={15} />
          {t('platform.cases.apply')}
        </button>
        <button type="button" className={styles.secondaryBtn} onClick={reload}>
          <Icon name="refresh" size={15} />
          {t('platform.dashboard.refresh')}
        </button>
      </form>

      <AsyncPanel
        state={state}
        onRetry={reload}
        isEmpty={() => sorted.length === 0}
        emptyTitle={t('platform.cases.emptyTitle')}
        emptyBody={t('platform.cases.emptyBody')}
      >
        {() => (
          <>
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <caption className={styles.tableCaption}>
                  {t('platform.cases.tableCaption', { n: sorted.length })}
                </caption>
                <thead>
                  <tr>
                    <SortableHeader
                      field="legalName"
                      label={t('platform.cases.colInstitution')}
                      activeField={sort.field}
                      direction={sort.order}
                      onSort={changeSort}
                    />
                    <th scope="col">{t('platform.cases.colType')}</th>
                    <SortableHeader
                      field="state"
                      label={t('platform.cases.colState')}
                      activeField={sort.field}
                      direction={sort.order}
                      onSort={changeSort}
                    />
                    <SortableHeader
                      field="targetActivationDate"
                      label={t('platform.cases.colTarget')}
                      activeField={sort.field}
                      direction={sort.order}
                      onSort={changeSort}
                    />
                    <SortableHeader
                      field="updatedAt"
                      label={t('platform.cases.colUpdated')}
                      activeField={sort.field}
                      direction={sort.order}
                      onSort={changeSort}
                    />
                  </tr>
                </thead>
                <tbody>
                  {visible.map((entry) => (
                    <tr key={entry.id}>
                      <th scope="row" className={styles.tableRowHeader}>
                        <Link to={`/admin/onboarding/${encodeURIComponent(entry.id)}`} className={styles.rowTitleLink}>
                          {caseDisplayName(entry)}
                        </Link>
                        <span className={styles.tableSub}>
                          {entry.caseReference || entry.institutionCode || entry.id}
                        </span>
                      </th>
                      <td>{typeLabel(entry.institutionType)}</td>
                      <td>
                        <StateBadge kind="onboarding" value={entry.state} size="sm" />
                      </td>
                      <td>
                        {/*
                          A target date already past on a case that is not live is
                          the row somebody has to act on, so it is called out
                          rather than left as another grey date.
                        */}
                        {entry.targetActivationDate ? (
                          <span
                            className={
                              isOverdue(entry) ? styles.outstandingPill : styles.mutedCell
                            }
                          >
                            {formatDate(entry.targetActivationDate, locale)}
                          </span>
                        ) : (
                          <span className={styles.mutedCell}>—</span>
                        )}
                      </td>
                      <td className={styles.mutedCell}>{formatRelative(entry.updatedAt, locale)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={currentPage}
              limit={DEFAULT_PAGE_SIZE}
              total={sorted.length}
              totalPages={totalPages}
              onPage={setPage}
              label={t('platform.cases.title')}
            />
          </>
        )}
      </AsyncPanel>

      <CreateCaseDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={(caseId) => navigate(`/admin/onboarding/${encodeURIComponent(caseId)}`)}
      />
    </div>
  );
}

/**
 * Sorting that puts unknowns last in both directions.
 *
 * A case with no `updatedAt` sorted as if it were the epoch would head the
 * "oldest first" list and be read as the most neglected case on the platform,
 * which is the opposite of what a missing timestamp means.
 */
function compareCases(a: OnboardingCaseSummary, b: OnboardingCaseSummary, field: CaseSortField): number {
  const left = field === 'legalName' ? caseDisplayName(a) : a[field];
  const right = field === 'legalName' ? caseDisplayName(b) : b[field];
  if (typeof left !== 'string' && typeof right !== 'string') return 0;
  if (typeof left !== 'string') return 1;
  if (typeof right !== 'string') return -1;
  return left.localeCompare(right);
}

/* ─── Create ─── */

const EMPTY_FORM: CreateOnboardingCaseRequest = {
  institutionName: '',
  countryCode: 'CM',
  institutionType: 'SECONDARY_SCHOOL',
  contactEmail: '',
  contactName: '',
  externalReference: '',
  notes: '',
};

function CreateCaseDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (caseId: string) => void;
}) {
  const { t } = useTranslation();
  const label = useEnumLabel();
  const [form, setForm] = useState<CreateOnboardingCaseRequest>(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [proposedCode, setProposedCode] = useState<string>('');
  const [codeLoading, setCodeLoading] = useState(false);

  const update = <K extends keyof CreateOnboardingCaseRequest>(field: K, value: CreateOnboardingCaseRequest[K]) =>
    setForm((current) => ({ ...current, [field]: value }));

  useEffect(() => {
    const name = form.institutionName.trim();
    const country = form.countryCode.trim().toUpperCase();
    if (name.length < 2 || !/^[A-Z]{2}$/.test(country)) {
      setProposedCode('');
      setCodeLoading(false);
      return;
    }
    let cancelled = false;
    setProposedCode('');
    setCodeLoading(true);
    const timer = window.setTimeout(() => {
      platformApi.onboarding.previewCode(name, country)
        .then((code) => { if (!cancelled) setProposedCode(code); })
        .catch(() => { if (!cancelled) setProposedCode(''); })
        .finally(() => { if (!cancelled) setCodeLoading(false); });
    }, 250);
    return () => { cancelled = true; window.clearTimeout(timer); };
  }, [form.institutionName, form.countryCode]);

  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.contactEmail);
  const blockers = [
    ...(form.institutionName.trim().length < 2 ? [t('platform.cases.blockerName')] : []),
    ...(!/^[A-Z]{2}$/.test(form.countryCode) ? [t('platform.cases.blockerCountry')] : []),
    ...(!proposedCode || codeLoading ? [t('platform.cases.blockerCode')] : []),
    ...(!emailValid ? [t('platform.cases.blockerEmail')] : []),
  ];
  const valid = blockers.length === 0;

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!valid || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      const created = await platformApi.onboarding.createCase({
        ...form,
        // Blank optionals are dropped: the server rejects an empty string where
        // it expects an absent field.
        contactName: form.contactName || undefined,
        externalReference: form.externalReference || undefined,
        notes: form.notes || undefined,
      });
      setForm(EMPTY_FORM);
      onClose();
      onCreated(created.id);
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title={t('platform.cases.createTitle')} description={t('platform.cases.createLead')} closeOnBackdropClick={false}>
      <form className={styles.dialogForm} onSubmit={submit} noValidate>
        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.cases.fieldName')}</span>
          <input
            className={styles.input}
            value={form.institutionName}
            required
            onChange={(event) => update('institutionName', event.target.value)}
          />
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.cases.fieldCountry')}</span>
          <input
            className={styles.input}
            value={form.countryCode}
            required
            autoComplete="off"
            maxLength={2}
            aria-describedby="country-code-hint"
            onChange={(event) => update('countryCode', event.target.value.toUpperCase())}
          />
          <span className={styles.fieldHint} id="country-code-hint">
            {t('platform.cases.fieldCountryHint')}
          </span>
        </label>

        <div className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.cases.fieldCode')}</span>
          <output className={styles.input} aria-live="polite">
            {codeLoading
              ? t('platform.cases.codeGenerating')
              : proposedCode || t('platform.cases.codeAwaitingName')}
          </output>
          <span className={styles.fieldHint}>{t('platform.cases.fieldCodeHint')}</span>
        </div>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.cases.fieldType')}</span>
          <Select
            value={form.institutionType}
            onChange={(value) => update('institutionType', value as InstitutionType)}
            options={INSTITUTION_TYPES.map((type) => ({
              value: type,
              label: label('institutionType', type),
            }))}
          />
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.cases.fieldContactEmail')}</span>
          <input
            className={styles.input}
            type="email"
            value={form.contactEmail}
            required
            aria-invalid={form.contactEmail.length > 0 && !emailValid}
            onChange={(event) => update('contactEmail', event.target.value)}
          />
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.cases.fieldContactName')}</span>
          <input className={styles.input} value={form.contactName ?? ''} onChange={(event) => update('contactName', event.target.value)} />
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.cases.fieldReference')}</span>
          <input
            className={styles.input}
            value={form.externalReference ?? ''}
            onChange={(event) => update('externalReference', event.target.value)}
          />
        </label>

        <label className={styles.field}>
          <span className={styles.fieldLabel}>{t('platform.cases.fieldNotes')}</span>
          <textarea
            className={styles.textarea}
            rows={3}
            value={form.notes ?? ''}
            onChange={(event) => update('notes', event.target.value)}
          />
        </label>

        {error && (
          <p className={styles.formError} role="alert">
            {error}
          </p>
        )}

        <p className={styles.fieldHint}>{t('platform.cases.createDraftNote', { states: OPEN_STATES.length })}</p>

        <div className="modal-actions">
          <button type="button" className="modal-button modal-button--secondary" onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </button>
            {valid && !submitting ? (
              <button type="submit" className="modal-button">
                {t('platform.cases.create')}
              </button>
            ) : (
              <Tooltip content={submitting ? t('platform.cases.blockerSubmitting') : blockers.join(' ')}>
                <button type="submit" className="modal-button" disabled>
                  {submitting ? t('platform.cases.creating') : t('platform.cases.create')}
                </button>
              </Tooltip>
            )}
        </div>
      </form>
    </Modal>
  );
}
